import {useGrowthBook} from "@growthbook/growthbook-react";
import gql from "graphql-tag";
import {useMemo} from "react";

import type {Result} from "@khanacademy/wonder-blocks-data";
import type {AppFeatures} from "@khan/analytics-growthbook/app-features";
import {useQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";

import type {guideLanguages_khanmigoSupportedKALocales as GuideLanguage} from "./__graphql-types__/guideLanguages.ts";
import type {guideLanguagesType} from "./__graphql-types__/index.ts";

export const GUIDE_LANGUAGES_QUERY: GqlOperation<
    guideLanguagesType["response"],
    guideLanguagesType["variables"]
> = gqlOp(gql`
    query guideLanguages {
        khanmigoSupportedKALocales {
            id
            khanmigoSupport
            displayName
            localName
        }
    }
`);

export interface GuideLanguagesResponse {
    guideLanguages: GuideLanguage[];
    defaultLanguageCode: string;
}

export const useGuideLanguages = (): Result<GuideLanguagesResponse> => {
    const [result] = useQuery(GUIDE_LANGUAGES_QUERY, {
        skip: !PermissionsInfo.isLoggedIn,
    });
    const growthbook = useGrowthBook<AppFeatures>();

    return useMemo(() => {
        if (result.status !== "success") {
            return result;
        }

        const includeAlpha = growthbook?.isOn("streaming_translation");
        const guideLanguages = result.data.khanmigoSupportedKALocales.filter(
            (lang) => (lang.khanmigoSupport === "ALPHA" ? includeAlpha : true),
        );
        const defaultLanguageCode = guideLanguages.find(
            (lang) => lang.id === RequestInfo.kaLocale,
        )
            ? RequestInfo.kaLocale
            : "en";

        return {
            status: "success",
            data: {
                guideLanguages,
                defaultLanguageCode,
            },
        };
    }, [result, growthbook]);
};
